import { useState, useEffect } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';
import RecordsUploadModal from '../components/RecordsUploadModal';
import AttachmentsModal from '../components/AttachmentsModal';
import SubmitToClientModal from '../components/SubmitToClientModal';
import RecordsNotesModal from '../components/RecordsNotesModal';
import { useAuth } from '../context/AuthContext';

// Format a stored DATE without timezone shift (the API sends UTC-midnight ISO strings).
function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString();
}

// Group completed jobs into collapsible folders by project-ID thousand range (5000s, 6000s, …)
function groupItems(list, open) {
  const groups = {};
  list.forEach(j => {
    const n = parseInt((String(j.project_number || '').match(/\d+/) || ['0'])[0], 10);
    const base = Math.floor(n / 1000) * 1000;
    (groups[base] = groups[base] || []).push(j);
  });
  const bases = Object.keys(groups).map(Number).sort((a, b) => b - a);
  const items = [];
  bases.forEach(base => {
    items.push({ type: 'header', base, count: groups[base].length });
    if (open[base]) groups[base].forEach(job => items.push({ type: 'job', job }));
  });
  return items;
}

// Days since a timestamp, and an aging color (fresh → stale)
function daysSince(ts) {
  if (!ts) return null;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
}
function AgeBadge({ job, status }) {
  if (status === 'Completed') return null;
  const anchor = status === 'Pending' ? job.created_at : (job.started_at || job.created_at);
  const age = daysSince(anchor);
  if (age === null) return null;
  const label = status === 'Pending' ? 'pending' : 'in progress';
  const color = age >= 7 ? '#ef4444' : age >= 3 ? '#facc15' : '#9ca3af';
  return (
    <span title={`${age} day${age === 1 ? '' : 's'} ${label}`} className="text-xs font-bold px-2 py-1 rounded border whitespace-nowrap" style={{ color, borderColor: `${color}66` }}>
      ⏱ {age}d
    </span>
  );
}

// Has CAD sent the client their deliverables? Mirrors Site Tracker's Client Deliverable
// Sent (A) date (refreshed by the server poll and on Sync). Records must not send
// anything to the client before CAD has, so an unstamped pending / in-progress job
// gets a red hold flag.
function CadBadge({ job, status }) {
  if (status === 'Completed') return null;
  if (job.client_deliverable_sent_date) {
    // Postgres DATE arrives as an ISO timestamp (2026-09-24T00:00:00.000Z) — keep the
    // calendar day and parse it as local midnight so it never shifts a day on display
    const d = new Date(String(job.client_deliverable_sent_date).slice(0, 10) + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return null;
    return (
      <span title={`CAD sent client deliverables — Client Deliverable Sent (A) ${d.toLocaleDateString()}`}
        className="text-xs font-bold px-2 py-1 rounded border whitespace-nowrap text-green-400 border-green-500/40 bg-green-500/10">
        CAD SENT {d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
      </span>
    );
  }
  return (
    <span title="CAD has not sent client deliverables yet (Client Deliverable Sent (A) is blank) — hold"
      className="text-xs font-bold px-2 py-1 rounded border whitespace-nowrap text-red-400 border-red-500/40 bg-red-500/10">
      CAD HOLD
    </span>
  );
}

export default function RecordsDashboard() {
  const { user } = useAuth();
  const readOnly = user?.role === 'upload';
  const [jobs, setJobs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [uploadJob, setUploadJob] = useState(null);
  const [attachJob, setAttachJob] = useState(null);
  const [clientJob, setClientJob] = useState(null);
  const [noteJob, setNoteJob] = useState(null);
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [sortField, setSortField] = useState('project'); // project | customer | program
  const [sortDir, setSortDir] = useState('asc');          // asc | desc
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (base) => setOpenGroups(p => ({ ...p, [base]: !p[base] }));

  useEffect(() => {
    fetchJobs();
    api.get('/records/users').then(({ data }) => setStaff(data.map(u => u.name))).catch(() => {});
    // Silent refetch so the CAD SENT / HOLD flag (refreshed server-side every 5 min) shows up without a reload
    const t = setInterval(() => { api.get('/records').then(({ data }) => setJobs(data)).catch(() => {}); }, 60000);
    return () => clearInterval(t);
  }, []);

  async function fetchJobs() {
    try {
      const { data } = await api.get('/records');
      setJobs(data);
    } finally {
      setLoading(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const { data } = await api.post('/records/sync');
      await fetchJobs();
      alert(`Synced ${data.synced} jobs from Site Tracker`);
    } catch {
      alert('Sync failed — check Site Tracker connection');
    } finally {
      setSyncing(false);
    }
  }

  async function resync(sfId) {
    try {
      const { data } = await api.post(`/records/${sfId}/resync`);
      setJobs(prev => prev.map(j => j.sf_id === sfId ? { ...j, ...data } : j));
      if (data.design_synced === false) alert('Site Tracker still rejected the update — try again shortly.');
    } catch { alert('Resync failed'); }
  }

  async function patchStatus(sfId, fields) {
    try {
      const { data } = await api.patch(`/records/${sfId}/status`, fields);
      setJobs(prev => prev.map(j => j.sf_id === sfId ? { ...j, ...data } : j));
    } catch { alert('Update failed'); }
  }

  // Mark complete, then offer to submit the project to the client
  async function completeAndAsk(job) {
    await patchStatus(job.sf_id, { status_811_design: 'Completed' });
    setClientJob(job);
  }

  async function handleUploaded() {
    await fetchJobs();
  }

  const counts = {
    all: jobs.length,
    pending: jobs.filter(j => j.status_811_design === 'Pending').length,
    in_progress: jobs.filter(j => j.status_811_design === 'In Progress').length,
    complete: jobs.filter(j => j.status_811_design === 'Completed').length,
  };

  // A search looks across every status, so nobody has to re-search on each tab
  const searching = search.trim() !== '';
  const pickStatus = (key) => { setFilterStatus(key); setSearch(''); };

  const filtered = jobs.filter(j => {
    const matchSearch = [j.project_number, j.customer, j.program, j.site_name, j.ticket_number]
      .join(' ').toLowerCase().includes(search.toLowerCase());
    const matchStatus = searching || filterStatus === 'all'
      ? true
      : filterStatus === 'pending'
      ? j.status_811_design === 'Pending'
      : filterStatus === 'in_progress'
      ? j.status_811_design === 'In Progress'
      : j.status_811_design === 'Completed';
    const matchAssigned = assignedFilter === 'all'
      ? true
      : assignedFilter === 'unassigned'
      ? !j.assigned_to
      : j.assigned_to === assignedFilter;
    return matchSearch && matchStatus && matchAssigned;
  }).sort((a, b) => {
    const key = j => (sortField === 'customer' ? j.customer : sortField === 'program' ? j.program : j.project_number) || '';
    const av = key(a);
    const bv = key(b);
    const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // In the Complete view, fold jobs into thousand-range folders
  const completeView = filterStatus === 'complete' && !searching;
  const listItems = completeView
    ? groupItems(filtered, openGroups)
    : filtered.map(job => ({ type: 'job', job }));

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white tracking-widest uppercase">Records</h1>
            <p className="text-gray-400 text-sm mt-1">Pending 811 jobs from Site Tracker{readOnly && ' · View only'}</p>
          </div>
          {!readOnly && (
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest rounded border transition disabled:opacity-50"
            style={{ borderColor: '#c9a84c', color: '#c9a84c' }}
          >
            {syncing ? 'Syncing…' : 'Sync from Site Tracker'}
          </button>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Pending', value: counts.pending, color: 'text-yellow-400', key: 'pending' },
            { label: 'In Progress', value: counts.in_progress, color: 'text-blue-400', key: 'in_progress' },
            { label: 'Complete', value: counts.complete, color: 'text-green-400', key: 'complete' },
          ].map(card => (
            <button
              key={card.label}
              onClick={() => pickStatus(card.key)}
              className="border rounded-lg p-4 text-left transition hover:border-gold-500"
              style={{ backgroundColor: '#0d1b2e', borderColor: filterStatus === card.key && !searching ? '#c9a84c' : '#1e3a5f' }}
            >
              <p className={`text-3xl font-black ${card.color}`}>{card.value}</p>
              <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">{card.label}</p>
            </button>
          ))}
        </div>

        {/* Status filter tabs + search */}
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {[
              { key: 'pending', label: 'Pending' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'complete', label: 'Complete' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => pickStatus(f.key)}
                className="px-3 py-1 rounded text-xs font-bold uppercase tracking-widest transition"
                style={filterStatus === f.key && !searching
                  ? { backgroundColor: '#c9a84c', color: '#0d1b2e' }
                  : { color: '#9ca3af' }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Assigned filter */}
          <select
            value={assignedFilter}
            onChange={e => setAssignedFilter(e.target.value)}
            className="border rounded px-2 py-1.5 text-xs text-white focus:outline-none"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          >
            <option value="all">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {staff.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Sort */}
          <select
            value={sortField}
            onChange={e => setSortField(e.target.value)}
            className="border rounded px-2 py-1.5 text-xs text-white focus:outline-none"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          >
            <option value="project">Sort: Project</option>
            <option value="customer">Sort: Customer</option>
            <option value="program">Sort: Program</option>
          </select>
          <button
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
            className="border rounded px-2 py-1.5 text-xs font-bold text-gold-400 focus:outline-none"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          >
            {sortDir === 'asc' ? '↑ A–Z' : '↓ Z–A'}
          </button>

          <input
            type="text"
            placeholder="Search all statuses…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 border rounded px-4 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          />
        </div>

        {searching && !loading && (
          <p className="text-xs text-gray-400">
            Searching Pending, In Progress and Complete · {filtered.length} match{filtered.length === 1 ? '' : 'es'}
          </p>
        )}

        {loading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500 text-sm">{searching ? 'No jobs match that search.' : 'No jobs found. Hit Sync to pull from Site Tracker.'}</p>
        ) : (
          <div className="space-y-2">
            {listItems.map(item => {
              if (item.type === 'header') {
                return (
                  <button
                    key={`folder-${item.base}`}
                    onClick={() => toggleGroup(item.base)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition hover:border-gold-500"
                    style={{ backgroundColor: '#13243a', borderColor: '#1e3a5f' }}
                  >
                    <span>📁</span>
                    <span className="text-white font-bold text-sm">{item.base}–{item.base + 999}</span>
                    <span className="text-gray-500 text-xs">({item.count})</span>
                    <span className="ml-auto text-gray-400 text-xs font-bold uppercase tracking-wide">{openGroups[item.base] ? '▲ Hide' : '▼ Show'}</span>
                  </button>
                );
              }
              const job = item.job;
              const status = job.status_811_design || 'Pending';

              return (
                <div
                  key={job.sf_id}
                  className="border rounded-lg overflow-hidden"
                  style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}
                >
                  {/* Main row */}
                  <div className="flex items-center gap-3 px-4 py-3">

                    {/* Status dot */}
                    <div className={`w-2 h-2 rounded-full shrink-0 ${
                      status === 'Completed' ? 'bg-green-400'
                      : status === 'In Progress' ? 'bg-blue-400'
                      : 'bg-yellow-400'
                    }`} title={status} />

                    {/* Status label — only while a search spans every status */}
                    {searching && (
                      <span className={`shrink-0 w-20 text-[10px] font-bold uppercase tracking-widest ${
                        status === 'Completed' ? 'text-green-400'
                        : status === 'In Progress' ? 'text-blue-400'
                        : 'text-yellow-400'
                      }`}>
                        {status === 'Completed' ? 'Complete' : status}
                      </span>
                    )}

                    {/* Job info */}
                    <div className="flex-1 min-w-0 grid grid-cols-5 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Project</p>
                        <p className="text-white font-mono font-semibold">{job.project_number || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Customer</p>
                        <p className="text-gray-300 truncate">{job.customer || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Program</p>
                        <p className="text-gray-300 truncate" title={job.program || ''}>{job.program || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Site Name</p>
                        <p className="text-gray-300 break-words">{job.site_name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Assigned</p>
                        {readOnly ? (
                          <p className="text-gray-300 truncate text-sm">{job.assigned_to || '—'}</p>
                        ) : (
                          <select
                            value={job.assigned_to || ''}
                            onChange={e => patchStatus(job.sf_id, { assigned_to: e.target.value })}
                            className="text-xs rounded px-1.5 py-0.5 border focus:outline-none w-full"
                            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: job.assigned_to ? '#fff' : '#6b7280' }}
                          >
                            <option value="">Unassigned</option>
                            {staff.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-2 shrink-0">

                      <CadBadge job={job} status={status} />
                      <AgeBadge job={job} status={status} />

                      {readOnly ? (
                        <>
                          {job.civil_grid && <span className="text-xs px-2 py-1 rounded border font-bold text-purple-400 border-purple-500/40 bg-purple-500/10">CG</span>}
                          <span className={`text-xs px-2 py-1 rounded border font-bold ${
                            job.ssp
                              ? 'text-green-400 border-green-500/40 bg-green-500/10'
                              : 'text-red-400 border-red-500/40 bg-red-500/10'
                          }`}>
                            SSP
                          </span>
                          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: status === 'Completed' ? '#4ade80' : status === 'In Progress' ? '#60a5fa' : '#facc15' }}>{status}</span>
                        </>
                      ) : (
                        <>
                          {/* Civil Grid toggle */}
                          <button
                            onClick={() => patchStatus(job.sf_id, { civil_grid: !job.civil_grid })}
                            title="Civil Grid"
                            className={`text-xs px-2 py-1 rounded border transition font-bold ${
                              job.civil_grid
                                ? 'text-purple-400 border-purple-500/40 bg-purple-500/10'
                                : 'text-gray-600 border-gray-700 hover:text-purple-400'
                            }`}
                          >
                            CG
                          </button>

                          {/* SSP toggle — red until the SSP step is done, green after */}
                          <button
                            onClick={() => patchStatus(job.sf_id, { ssp: !job.ssp })}
                            title="SSP"
                            className={`text-xs px-2 py-1 rounded border transition font-bold ${
                              job.ssp
                                ? 'text-green-400 border-green-500/40 bg-green-500/10'
                                : 'text-red-400 border-red-500/40 bg-red-500/10 hover:text-green-400'
                            }`}
                          >
                            SSP
                          </button>

                          {/* Status actions */}
                          {status === 'Pending' && (
                            <button
                              onClick={() => patchStatus(job.sf_id, { status_811_design: 'In Progress', assigned_to: user.name })}
                              className="text-xs px-2 py-1 rounded font-bold uppercase tracking-wide transition"
                              style={{ backgroundColor: '#1e3a5f', color: '#c9a84c', border: '1px solid #c9a84c' }}
                            >
                              Accept
                            </button>
                          )}
                          {status === 'In Progress' && (
                            <>
                              <button
                                onClick={() => completeAndAsk(job)}
                                className="text-xs px-2 py-1 rounded font-bold uppercase tracking-wide transition"
                                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
                              >
                                Done
                              </button>
                              <button
                                onClick={() => patchStatus(job.sf_id, { status_811_design: 'Pending' })}
                                className="text-xs px-2 py-1 rounded border text-gray-500 border-gray-700 hover:text-white transition"
                              >
                                ↩
                              </button>
                            </>
                          )}
                          {status === 'Completed' && (
                            <span className="text-xs text-green-400 font-bold">✓ Done</span>
                          )}

                          {/* Upload — only once the job is In Progress */}
                          {status === 'In Progress' && (
                            <button
                              onClick={() => setUploadJob(job)}
                              className="px-3 py-1 text-xs font-bold uppercase tracking-wide rounded border transition"
                              style={{ borderColor: '#c9a84c', color: '#c9a84c' }}
                            >
                              + Upload
                            </button>
                          )}
                        </>
                      )}

                      {/* Site Tracker sync failure — retry */}
                      {!readOnly && job.design_synced === false && (
                        <button
                          onClick={() => resync(job.sf_id)}
                          title="Site Tracker status push failed — click to retry"
                          className="text-[11px] font-bold px-2 py-1 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 transition"
                        >
                          ⚠ Retry sync
                        </button>
                      )}

                      {/* Staged utility records on the site — anyone can view/download */}
                      {(job.staged_count > 0 || job.last_submission) && (
                        <button
                          onClick={() => setUploadJob(job)}
                          title={job.last_submission ? `Submitted ${new Date(job.last_submission.created_at).toLocaleDateString()} — ${job.last_submission.zip_name}` : 'Utility records staged on the site'}
                          className="text-xs px-2 py-1 rounded border transition hover:text-white"
                          style={{ borderColor: '#1e3a5f', color: job.last_submission?.pushed_to_sf ? '#4ade80' : '#c9a84c' }}
                        >
                          📁 Records{job.staged_count > 0 ? ` · ${job.staged_count}` : ''}
                        </button>
                      )}

                      {/* Files — all Site Tracker attachments (like CAD) */}
                      <button
                        onClick={() => setAttachJob(job)}
                        title="View Site Tracker attachments"
                        className="text-xs px-2 py-1 rounded border transition text-gray-400 hover:text-gold-400"
                        style={{ borderColor: '#1e3a5f' }}
                      >
                        📎 Files
                      </button>
                    </div>
                  </div>

                  {/* Second line — scheduled + address + 811 ticket # */}
                  <div className="px-4 pb-3 flex flex-wrap gap-4">
                    <div className="w-28 shrink-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Scheduled</p>
                      <p className="text-sm" style={{ color: job.scheduled_date ? '#c9a84c' : '#6b7280' }}>
                        {fmtDate(job.scheduled_date)}
                      </p>
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Address</p>
                      {readOnly ? (
                        <p className="text-gray-300 text-sm">{job.address || '—'}</p>
                      ) : (
                        <input
                          defaultValue={job.address || ''}
                          onBlur={e => { const v = e.target.value.trim(); if (v !== (job.address || '')) patchStatus(job.sf_id, { address: v }); }}
                          placeholder="Add address…"
                          className="text-sm rounded px-2 py-1 border focus:outline-none w-full"
                          style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: '#fff' }}
                        />
                      )}
                    </div>
                    <div className="w-32 shrink-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">811 Ticket #</p>
                      {readOnly ? (
                        <p className="text-gray-300 text-sm">{job.ticket_number || '—'}</p>
                      ) : (
                        <input
                          defaultValue={job.ticket_number || ''}
                          onBlur={e => { const v = e.target.value.trim(); if (v !== (job.ticket_number || '')) patchStatus(job.sf_id, { ticket_number: v }); }}
                          placeholder="Ticket #…"
                          className="text-sm rounded px-2 py-1 border focus:outline-none w-full"
                          style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: '#fff' }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Notes</p>
                      <button
                        onClick={() => setNoteJob(job)}
                        title={job.notes || (readOnly ? 'No note' : 'Add a note')}
                        className={`text-sm rounded px-2 py-1 border w-full text-left truncate transition ${job.notes ? 'text-gray-200 hover:border-gold-500' : 'text-gray-500 hover:text-gray-300'}`}
                        style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
                      >
                        {job.notes ? job.notes : (readOnly ? '—' : '+ note')}
                      </button>
                    </div>
                  </div>

                  {/* Completion date */}
                  {job.completed_at && job.status_811_design === 'Completed' && (
                    <div className="px-5 pb-2">
                      <p className="text-xs text-green-500">
                        Completed {new Date(job.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {uploadJob && (
        <RecordsUploadModal
          job={uploadJob}
          readOnly={readOnly}
          onClose={() => setUploadJob(null)}
          onChanged={handleUploaded}
          onSubmitted={(updated) => {
            // Backend already marked the job Completed and pushed the zip; refresh the row and offer the client submit
            setJobs(prev => prev.map(j => j.sf_id === updated.sf_id ? { ...j, ...updated } : j));
            fetchJobs();
            setClientJob(uploadJob);
          }}
        />
      )}

      {attachJob && (
        <AttachmentsModal
          title={attachJob.project_number}
          subtitle={attachJob.site_name}
          listUrl={`/records/${attachJob.sf_id}/attachments`}
          downloadUrl={(id) => `/records/${attachJob.sf_id}/attachments/${id}/download`}
          onClose={() => setAttachJob(null)}
        />
      )}

      {clientJob && (
        <SubmitToClientModal
          projectId={clientJob.project_number}
          onClose={() => setClientJob(null)}
        />
      )}

      {noteJob && (
        <RecordsNotesModal
          job={noteJob}
          readOnly={readOnly}
          onClose={() => setNoteJob(null)}
          onSave={async ({ notes, utilities }) => { await patchStatus(noteJob.sf_id, { notes, utilities }); }}
        />
      )}
    </Layout>
  );
}
